/**
 * useCheckIn Hook
 * Handles dealer check-in validation and submission logic.
 *
 * Three flows:
 *   1. Non-HO-Auditor, non-LCD dealer  → validate → SF CheckIn_VisitPlan → success toast.
 *   2. Non-HO-Auditor, ADOPTED LCD     → validate → BPI eligibility check → OPEN SURVEY MODAL.
 *                                        User submits survey → SF CheckIn_VisitPlan → POST LCD survey → success toast.
 *   3. HO Auditor                       → validate → SF CheckIn_VisitPlan → open audit popup → CheckIn_VisitPlanUpdate.
 *
 * The LCD survey runs BEFORE the Salesforce check-in (CR clarification: "survey
 * should happen first; only once the survey is done, check in API should be called").
 */

import { useCallback, useMemo } from "react";
import { myDealerService } from "../services/myDealerService";

export const useCheckIn = ({
  dealer,
  dealerCode,
  distanceToDealer,
  currentAccuracy,
  accuracy,
  territoryCheckIn,
  tsiCode,
  currentLatitude,
  currentLongitude,
  dealerLatitude,
  dealerLongitude,
  userType,
  userEmail,
  setLoading,
  showSuccess,
  showError,
  setCheckinFlag,
  setReloadDealerStatusFlag,
  setAutoCheckInFlag,
  setIsDealerPopupOpen,
  setRecordId,
  autoCheckInFlag,
  // LCD survey integration (optional — when absent, behavior is unchanged)
  checkLcdEligibility,
  openLcdSurvey,
  clusterCode,
  clusterName,
}) => {
  const isValidCheckIn = useMemo(() => {
    const calculatedDistance = 2 * (accuracy + currentAccuracy) + 300;
    const isEmpty = (val) => !val || val === "" || val === null || val === undefined;

    return distanceToDealer <= calculatedDistance ||
           (isEmpty(dealerLatitude) && isEmpty(dealerLongitude))
      ? "Valid"
      : "Invalid";
  }, [distanceToDealer, accuracy, currentAccuracy, dealerLatitude, dealerLongitude]);

  const validateCheckIn = useCallback(() => {
    if (currentLatitude === 0 && currentLongitude === 0) {
      return { isValid: false, message: "Please turn on your location" };
    }
    if (!territoryCheckIn) {
      return { isValid: false, message: "Please select a territory" };
    }
    if (!dealer || !dealerCode) {
      return { isValid: false, message: "Please select a dealer" };
    }

    const isValidCoordinates = (val) =>
      typeof val === "number" && !isNaN(val) && val !== 0;
    const latValid = isValidCoordinates(parseFloat(dealerLatitude));
    const longValid = isValidCoordinates(parseFloat(dealerLongitude));
    if (!(latValid && longValid)) {
      return {
        isValid: false,
        message: "Unregistered dealer. Registration to be done via Proton",
      };
    }

    const maxDistanceThreshold = 2000;
    if (distanceToDealer > maxDistanceThreshold) {
      return {
        isValid: false,
        message: "Check-in unsuccessful, distance to dealer location beyond permissible limit",
      };
    }

    return { isValid: true, message: "" };
  }, [
    currentLatitude,
    currentLongitude,
    territoryCheckIn,
    dealer,
    dealerCode,
    dealerLatitude,
    dealerLongitude,
    distanceToDealer,
  ]);

  /**
   * Build the Salesforce CheckIn_VisitPlan payload from current hook inputs.
   */
  const buildCheckInPayload = useCallback(
    () => ({
      RecordtypeName: "Visit Plan",
      territory: territoryCheckIn,
      dealer,
      dealercode: dealerCode,
      customer: "",
      customercode: "",
      Transaction_lat: currentLatitude,
      Transaction_long: currentLongitude,
      IsValidCheckIn: isValidCheckIn,
      Master_lat: dealerLatitude,
      Master_long: dealerLongitude,
      Trasanction_distance: distanceToDealer,
      Master_Accuracy: accuracy,
      Current_Accuracy: currentAccuracy,
      Is_Correct_CheckIn: "",
      Dealer_Check_in: "",
      non_darphan_bill_file_url: "",
    }),
    [
      territoryCheckIn,
      dealer,
      dealerCode,
      currentLatitude,
      currentLongitude,
      isValidCheckIn,
      dealerLatitude,
      dealerLongitude,
      distanceToDealer,
      accuracy,
      currentAccuracy,
    ]
  );

  /**
   * Calls Salesforce CheckIn_VisitPlan. Returns { success, recordId, message }.
   * The caller decides what to do next (finalize, open audit popup, save survey).
   */
  const executeSalesforceCheckIn = useCallback(async () => {
    const payload = buildCheckInPayload();
    const response = await myDealerService.checkInToDealer(payload);
    return {
      success: !!response.success,
      recordId: response?.data?.id || "",
      message: response.message,
    };
  }, [buildCheckInPayload]);

  /**
   * Shared "check-in complete" side-effects: toast + Redux/flag reload.
   */
  const finalizeCheckInSuccess = useCallback(() => {
    showSuccess(`Successfully checked-in for ${dealer ? dealer : "this"} dealer!`);
    setAutoCheckInFlag(false);
    if (autoCheckInFlag) {
      setCheckinFlag(true);
      // Fire-and-forget: auto-approve pending verification for SH3
      if (userType === "SH3") {
        myDealerService.triggerCheckInVerification(
          dealerCode,
          tsiCode,
          userType,
          userEmail
        );
      }
    }
    setReloadDealerStatusFlag(true);
  }, [
    dealer,
    dealerCode,
    tsiCode,
    userType,
    userEmail,
    autoCheckInFlag,
    showSuccess,
    setAutoCheckInFlag,
    setCheckinFlag,
    setReloadDealerStatusFlag,
  ]);

  /**
   * Entry point — fired by the Check In button.
   *
   * Order of operations for a Branch Manager (non-HO-Auditor):
   *   1. Validate.
   *   2. Ask BPI: is this dealer an adopted LCD for this BM?
   *   3a. If yes → open survey modal. Salesforce check-in is DEFERRED.
   *       The modal's submit handler will call `executeCheckInAndSaveSurvey`.
   *   3b. If no  → proceed straight to Salesforce check-in (legacy path).
   */
  const performCheckIn = useCallback(
    async () => {
      const validation = validateCheckIn();
      if (!validation.isValid) {
        showError(validation.message);
        return;
      }

      if (isValidCheckIn === "Invalid") {
        showError("Dealer Check-in Unsuccessful");
        setAutoCheckInFlag(false);
        return;
      }

      // ── HO Auditor: keep legacy 2-step flow (SF first, then audit popup) ──
      if (userType === "HO Auditor") {
        try {
          setLoading(true);
          const sf = await executeSalesforceCheckIn();
          setLoading(false);
          if (sf.success) {
            setRecordId(sf.recordId);
            setIsDealerPopupOpen(true);
          } else {
            showError(sf.message || "Check-in failed");
            setAutoCheckInFlag(false);
          }
        } catch (error) {
          console.error("Error during check-in:", error);
          showError(error.message || "Check-in failed");
          setAutoCheckInFlag(false);
          setLoading(false);
        }
        return;
      }

      // ── Non-HO-Auditor: LCD survey gate runs BEFORE Salesforce check-in ──
      if (checkLcdEligibility && openLcdSurvey) {
        try {
          setLoading(true);
          const eligibility = await checkLcdEligibility({ dealerCode });
          setLoading(false);

          // Survey already submitted today for this dealer → don't re-prompt;
          // fall through to the regular SF check-in path below.
          if (eligibility?.eligible && eligibility?.alreadyDoneToday) {
            // do nothing here, drop to the non-LCD path
          } else if (eligibility?.eligible) {
            openLcdSurvey({
              dealerCode,
              dealerName: dealer,
              clusterCode,
              clusterName,
              finyear: eligibility.finyear,
              quarter: eligibility.quarter,
              // Pre-fill answers from the most recent submission for this
              // (user, dealer). null if there's no prior survey.
              previousAnswers: eligibility.previousAnswers || null,
              // Modal calls this on Submit. It runs the SF check-in, returns the
              // resulting recordId, and (on SF success) finalizes the UI.
              executeCheckIn: async () => {
                try {
                  setLoading(true);
                  const sf = await executeSalesforceCheckIn();
                  if (!sf.success) {
                    setLoading(false);
                    showError(sf.message || "Check-in failed");
                    setAutoCheckInFlag(false);
                    return { success: false, recordId: "" };
                  }
                  return { success: true, recordId: sf.recordId };
                } catch (error) {
                  setLoading(false);
                  console.error("Error during check-in:", error);
                  showError(error.message || "Check-in failed");
                  setAutoCheckInFlag(false);
                  return { success: false, recordId: "" };
                }
              },
              // Called by the modal after both SF check-in AND survey POST succeed.
              onAllSuccess: () => {
                setLoading(false);
                finalizeCheckInSuccess();
              },
            });
            return;
          }
        } catch (err) {
          setLoading(false);
          // Per scope §5.4: BPI fetch fail is non-blocking — fall through to
          // a regular check-in without the survey.
          console.warn("LCD eligibility lookup failed; proceeding without survey", err);
        }
      }

      // ── Non-LCD dealer (or LCD wiring absent): regular check-in path ──
      try {
        setLoading(true);
        const sf = await executeSalesforceCheckIn();
        setLoading(false);
        if (sf.success) {
          finalizeCheckInSuccess();
        } else {
          showError(sf.message || "Check-in failed");
          setAutoCheckInFlag(false);
        }
      } catch (error) {
        console.error("Error during check-in:", error);
        showError(error.message || "Check-in failed");
        setAutoCheckInFlag(false);
        setLoading(false);
      }
    },
    [
      validateCheckIn,
      isValidCheckIn,
      userType,
      dealer,
      dealerCode,
      clusterCode,
      clusterName,
      setLoading,
      showError,
      setAutoCheckInFlag,
      setRecordId,
      setIsDealerPopupOpen,
      executeSalesforceCheckIn,
      finalizeCheckInSuccess,
      checkLcdEligibility,
      openLcdSurvey,
    ]
  );

  /**
   * HO Auditor: update the existing record with audit data. Unchanged behavior.
   */
  const proceedHOAuditorStampIn = useCallback(
    async (dealerPopupData, recordId) => {
      try {
        setLoading(true);

        const updatePayload = {
          Record_ID: recordId,
          Dealer_Check_in: dealerPopupData?.Dealer_Check_in || "",
        };

        if (dealerPopupData?.non_darphan_bill_file_url) {
          updatePayload.non_darphan_bill_file_url = dealerPopupData.non_darphan_bill_file_url;
        }

        const response = await myDealerService.updateCheckIn(updatePayload);

        setLoading(false);

        if (response.success) {
          setIsDealerPopupOpen(false);
          finalizeCheckInSuccess();
        } else {
          showError(response.message || "Update failed");
          setAutoCheckInFlag(false);
        }
      } catch (error) {
        console.error("Error during HO Auditor stamp-in:", error);
        showError(error.message || "Update failed");
        setLoading(false);
        setAutoCheckInFlag(false);
      }
    },
    [
      setLoading,
      showError,
      setAutoCheckInFlag,
      setIsDealerPopupOpen,
      finalizeCheckInSuccess,
    ]
  );

  const handleCheckInClick = useCallback(() => {
    performCheckIn();
  }, [performCheckIn]);

  return {
    validateCheckIn,
    performCheckIn,
    proceedHOAuditorStampIn,
    handleCheckInClick,
    isValidCheckIn,
  };
};

export default useCheckIn;
